import { Constraint, FieldControl, FieldJson, FormJson } from 'edocatbase/Form';
import { forEach, forEachObjIndexed, is, map, path } from 'ramda';
import { Control } from '../../../../search-screen/types/commons';
import { customTableSettings } from '../../../utils/customTableSettingsDeleteWhenPossible';
import { FieldJsonExtension } from '../types/types';

const moment = window.moment;

const hasValue = (value: any) => value || value === 0;

const replaceUrlParams = (params: any) => {
	let url = params.constraint_url;

	if (params.constraint_replace_url_params) {
		const paramsToReplace = params.constraint_replace_url_params.split('|#alf#|');
		forEach((param: string) => {
			const index = param.split(',')[0];
			const value = path(param.split(',')[1].trim().split('.'), window) || '';
			url = url.replace(index, value);
		}, paramsToReplace);
	}

	return url;
};

const getConstraints = (formJson: FormJson) => {
	const constraints = formJson.noValidate ? [] : formJson.constraints.slice();

	forEachObjIndexed((item: FieldJson) => {
		const template = item?.control?.template;

		if (template?.endsWith('double.ftl') || template?.endsWith('number.ftl')) {
			constraints.push({
				fieldId: item.id,
				id: 'NUMBER',
				validationHandler: `Alfresco.forms.validation.${item.endpointType === 'long' ? 'number' : 'double'}`,
			});
		}

		if (template?.endsWith('date.ftl')) {
			constraints.push({
				fieldId: item.id,
				id: 'DATE',
			});
		}

		if (formJson.noValidate) {
			if (item?.control?.params?.constraint) {
				const indexId = constraints.findIndex((constraint: Constraint) => {
					return constraint.fieldId === item.id && constraint.id === item.control.params.constraint;
				});

				if (indexId === -1) {
					const {
						constraint_message,
						constraint_container,
						constraint,
						constraint_conditioned_mandatory,
					} = item.control.params;

					constraints.push({
						fieldId: item.id,
						id: constraint,
						params: {
							constraint_message: constraint_message ? constraint_message : constraint,
							constraint_container: constraint_container,
							constraint_url: replaceUrlParams(item.control.params),
							constraint_conditioned_mandatory: constraint_conditioned_mandatory,
						},
					});
				}
			}
		}
	}, formJson.fields);


	return constraints;
};

const getFieldValue = (
	displayValue: any,
	value: any,
	control: FieldControl,
	repeating: boolean,
	dataType: string,
	isDefValues: boolean,
	fields: { [key: string]: FieldJson & FieldJsonExtension },
	type: string,
	defaultValue: string = '',
	mode?: string,
	label?: string
) => {
	const template = control.template;
	let newValue;
	try {
		if (mode !== 'view' && control?.params?.predefinedValueString && !isDefValues && !hasValue(value)) {
			const params = control.params.predefinedValueString.split('|edocat|');
			return params
				.map((row: string) => {
					return row.includes(',') ? path(row.split(','), window) || [] : row;
				})
				.join('');
		}
		if (dataType === 'double' && (template.endsWith('number.ftl') || template.endsWith('double.ftl'))) {
			const initValue = displayValue === '' ? value : displayValue;
			return initValue ? initValue : value;
		}

		if (template.endsWith('tableMM.ftl')) {
			if (!value) return [];
			try {
				return JSON.parse(value);
			} catch (e) {
				console.warn('cannot parse table value, table name: ', control.params?.tableID);
				return [];
			}
		}
		if (control.params?.format === 'postalCode') {
			if (!value) return defaultValue;
			return value.substring(0, 3) + ' ' + value.substring(3);
		}
		if (template.endsWith('aresfield.ftl')) {
			if (type === 'search') return '';
			const defaultValue = [
				{
					companyName: '',
					ico: '',
					dic: '',
					town: '',
					street: '',
					zip: '',
					aresError: false,
					contacts: [],
				},
			];

			if (!value) return defaultValue;
			try {
				return JSON.parse(value);
			} catch (e) {
				console.warn('cannot parse ares values');
				return [];
			}
		}

		if (template.endsWith('datediff.ftl')) {
			const fieldToWatch = control?.params?.fieldToWatch;
			const fieldToWatchValue = fieldToWatch ? fields[fieldToWatch].value : null;

			if (hasValue(value)) {
				const date = moment(value);
				const days = moment(date).startOf('day').diff(moment(fieldToWatchValue).startOf('day'), 'days');

				return { days, date };
			}

			const initialDiff = control?.params?.initialDiff || 1;

			return {
				days: initialDiff,
				date: fieldToWatchValue ? moment(moment(fieldToWatchValue).add(initialDiff, 'days')) : null,
			};
		}

		if (template.endsWith('databox.ftl')) {
			if (!hasValue(value)) {
				return null;
			}
			const labelField = control.params.initLabelField;

			return {
				label: labelField ? fields[labelField]?.value : value,
				value,
			};
		}

		if (template.endsWith('taskowner.ftl')) {
			if (!hasValue(value)) {
				return null;
			}

			const label = `${value.split('|')[1]} ${value.split('|')[2] || ''}`;

			return {
				label,
				value: value.split('|')[0],
			};
		}

		if (
			template.endsWith('daterange.ftl') ||
			((template.endsWith('date.ftl') || dataType === 'date') && type === 'search')
		) {
			if (hasValue(value)) {
				if (is(Object, value)) {
					return {
						start: value.minValue === null ? null : moment(value.minValue),
						end: value.maxValue === null ? null : moment(value.maxValue),
					};
				} else {
					return {
						start: moment(value.split('|')[0]),
						end: moment(value.split('|')[1]),
					};
				}
			}

			return {
				start: null,
				end: null,
			};
		}

		if (template.endsWith('priority.ftl')) {
			if (value) {
				const values = ['high', 'medium', 'low'];
				const text = values[value - 1];

				return {
					label: window.Alfresco.messages.global['priority.' + text],
					value: value && value.toString ? value.toString() : value,
				};
			}

			return {
				label: window.Alfresco.messages.global['priority.medium'],
				value: '2',
			};
		}

		if (template.endsWith('date.ftl') || dataType === 'date') {
			return value ? moment(value) : defaultValue ? moment(defaultValue) : null;
		}

		if ((template.endsWith('checkbox.ftl') || template.endsWith('email-notification.ftl')) && type !== 'search') {
			const initValue = value === '' ? defaultValue : value ?? defaultValue;
			if (is(String, initValue)) {
				return initValue === 'true';
			}
			return !!initValue;
		}
		if ((template.endsWith('checkbox.ftl') || template.endsWith('email-notification.ftl')) && type === 'search') {
			return value;
		}

		if (
			template.endsWith('category.ftl') ||
			template.endsWith('selectmany.ftl') ||
			template.endsWith('association.ftl') ||
			// authority multi
			(repeating && (template.endsWith('authority.ftl') || template.endsWith('asyncselect.ftl')))
		) {
			//If both defaultValue and value are faulsy, no value is shown to the user.
			if (!hasValue(value) && !hasValue(defaultValue)) return [];

			//If custom value is present, return it under any circumstances.
			if (value) {
				if (Array.isArray(value)) {
					return value;
				}
				return value.split(',');
			}

			//If there is no custom value, and the field that is being created belongs to initialFields, return an empty value,
			//as initialFields should preserve its initial state.
			if (isDefValues) {
				return [];
			}

			//If there is no custom value and there is defaultValue and the field is being created to display it to the user,
			//return defaultValue.
			if (Array.isArray(defaultValue)) {
				return defaultValue;
			}
			return defaultValue.split(',');
		}

		if (hasValue(value)) {
			if (is(Object, value)) {
				newValue = value;
			} else {
				newValue = `${value}`;
			}
		} else if (mode && mode !== 'view' && defaultValue) {
			newValue = defaultValue;
		} else {
			newValue = value;
		}
	} catch (err) {
		console.error(err);
	}
	return newValue;
};

function checkIfChanged(field: any, isDefValues: boolean, formJson: FormJson) {
	const template = field.control.template;
	if (template.endsWith('double.ftl')) {
		return true;
	}

	return null;
}

const getFieldOptions = (template: string, params: any, value: any, fieldOptions?: [] | string) => {
	if (template.endsWith('taskowner.ftl')) {
		if (!hasValue(value)) {
			return [];
		}

		const label = value.split('|')[1];
		const val = value.split('|')[0];
		return [
			{
				label,
				value: val,
			},
		];
	}

	if (fieldOptions) return fieldOptions;
	if (Array.isArray(params.options)) return params.options;

	const options = params.options.split(params.optionSeparator);
	const cleanOptions = options.filter((item?: string) => {
		return !(!item || !item.includes('|') || item.split('|')[1].trim() === '');
	});

	return map(
		item => ({
			label: item.split('|')[1],
			value: item.split('|')[0],
		}),
		cleanOptions
	);
};

const getFieldDisabled = (isDisabled: boolean, control: FieldControl, formJsonGroups?: string[]) => {
	if (isDisabled) {
		return true;
	}

	if (control.params && control.params.isEnabledByGroup) {
		const groups = control.params.isEnabledByGroup.split(',');
		return !groups.some((group: string) => formJsonGroups?.includes(group));
	}

	return false;
};

const getCollateralChangeFields = (fields: [] | string, delimiter: string) => {
	if (!fields) return [];
	if (Array.isArray(fields)) return fields;

	return fields.split(delimiter);
};

const getCollateralChange = (row: FieldJson) => {
	if (!row.control.params) {
		return null;
	}
	const params = row.control.params;

	const field = params.collateralChangeField;
	const fields = getCollateralChangeFields(params.collateralChangeFields, ',');
	const equalFields = getCollateralChangeFields(params.collateralChangeEqualFields, '|#alf#|');
	const rule = params.collateralChangeRule || 'default';

	return {
		field,
		fields,
		equalFields,
		rule,
	};
};

const getUpdateOnChange = (row: FieldJson) => {
	if (!row.control.params || !row.control.params.updateOptionsOnChange) {
		return null;
	}

	const params = row.control.params;

	const url = params.updateOptionsOnChangeUrl;
	const field = params.updateOptionsOnChangeField;

	return { field, url };
};

const getFieldControl = (control: Control, id: string) => {
	if (!control?.template?.endsWith('tableMM.ftl')) {
		return control;
	}

	const tableHeaders =
		control?.params?._tableItem?.colConfig.reduce((acc, it: any) => {
			if (it.json?.hiddenField) {
				return acc;
			}

			return [
				...acc,
				{
					key: it.col,
					label: control?.params?._fields[it.col].label,
					type: it.config?.type,
				},
			];
		}, []) || [];

	const formJson = {
		fields: customTableSettings[id]?.getFields(control?.params?._fields) || control?.params?._fields,
		constraints: customTableSettings[id]?.constraints || control?.params?._constraints,
		structure: [
			{
				id: 'default',
				kind: 'set',
				children: tableHeaders.map((it: any) => ({
					kind: 'field',
					id: it.key,
				})),
			},
		],
	};

	return {
		...control,
		params: {
			tableHeaders,
			formJson,
		},
	};
};

const createFormFields = (
	fields: { [key: string]: FieldJson & FieldJsonExtension },
	isDefValues: boolean,
	formJson: FormJson
) => {
	return map(
		field => ({
			dirty: false,
			focus: false,
			name: field.name,
			helpText: field.helpText,
			label: field.label,
			mandatory: field.mandatory,
			autoFocus: field.autoFocus,
			placeholder: field.placeholder,
			configName: field.configName,
			repeating: field.repeating,
			useDefaultValue: !field.value,
			dataType: field.dataType,
			dateFormat: field.dateFormat,
			excludedOptions: field.excludedOptions,
			excludedOptionsFieldName: field.excludedOptionsFieldName,

			init: field.init || [],
			error: field.error || false,
			multiple: field.multiple || false,
			updateOptionsOnChange: getUpdateOnChange(field),
			control: getFieldControl(field.control, field.id),
			disabled: getFieldDisabled(field.disabled, field.control, formJson.groups),
			wasChanged: checkIfChanged(field, isDefValues, formJson),
			collateralChange: field.collateralChange || getCollateralChange(field),

			modalRoot:
				field.control?.params?.rootNode !== '{siteshome}' ? field.control?.params?.rootNode : 'alfresco/sites/home',
			options:
				(field.control.params && field.control.params.options) || field.options
					? getFieldOptions(field.control.template, field.control.params, field.value, field.options)
					: [],
			value: getFieldValue(
				field?.fieldValue?.displayValue,
				field.value,
				field.control,
				field.repeating,
				field.dataType,
				isDefValues,
				fields,
				formJson.type,
				field.defaultValue,
				formJson.mode,
				field.label
			),
			lastValue: getFieldValue(
				field?.fieldValue?.displayValue,
				field.value,
				field.control,
				field.repeating,
				field.dataType,
				isDefValues,
				fields,
				formJson.type,
				field.defaultValue,
				formJson.mode,
				field.label
			),
		}),
		// @ts-ignore
		fields
	);
};

const createInitialState = (
	formJson: FormJson & { showWFTemplateButton: boolean; onTemplateButtonClick: Function },
	formInstanceId: string
) => {
	return {
		allowChangeAfterSubmit: formJson.allowChangeAfterSubmit || false,
		arguments: formJson.arguments,
		autoComplete: formJson.autoComplete,
		backButtonAction: formJson.backButtonAction,
		constraints: getConstraints(formJson),
		disabled: formJson.disabled || false,
		edit: false,
		fields: createFormFields(formJson.fields, false, formJson),
		formInstanceId: formInstanceId,
		hasErrors: false,
		hideCancelButton: formJson.hideCancelButton || false,
		hideSubmitButton: formJson.hideSubmitButton || false,
		hideSubmitButtons: formJson.hideSubmitButtons || false,
		showWFTemplateButton: formJson.showWFTemplateButton ?? false,
		onTemplateButtonClick: formJson.onTemplateButtonClick,
		initialFields: createFormFields(formJson.fields, formJson.isDefValues, formJson),
		inlineEditable: formJson.inlineEditable || false,
		method: formJson.method,
		mode: formJson.mode,
		noValidate: formJson.noValidate ?? false,
		propagateResponse: formJson.propagateResponse ?? false,
		sendForward: formJson.sendForward,
		stayOnPage: formJson.stayOnPage ?? false,
		structure: formJson.structure,
		submissionUrl: formJson.submissionUrl,
		submissionUrlFieldValue: formJson.submissionUrlFieldValue,
		submitted: false,
		submitButtonLabel: formJson.submitButtonLabel || window.Alfresco.messages.global['button.save'],
		submittedButtonLabel: formJson.submittedButtonLabel || window.Alfresco.messages.global['label.saved'],
		successCallback: formJson.successCallback,
		transitionSubmit: false,
		type: formJson.type || '',
		urlAfterSubmit: formJson.urlAfterSubmit,
		validating: false,
	};
};

export default createInitialState;
